import React from 'react';
import type { AdDisclosureCell, FirstFiveDaysViews } from '@discovr/types';
import { adMentionedDisplay, firstFiveDaysDisplay, type AdTone } from './firstFiveAdView';
import { SimpleTooltip } from '../components/ui/tooltip';
import { cn } from '../lib/utils';

const HINT_TOOLTIP_CLASS = 'max-w-64 whitespace-normal';

// A real button so the explanation opens on keyboard focus and tap, not only
// on hover; the sr-only copy gives screen readers the same explanation.
function HintTrigger({
  hint,
  className,
  children,
}: {
  hint: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <SimpleTooltip content={hint} contentClassName={HINT_TOOLTIP_CLASS}>
      <button
        type="button"
        className={cn(
          'focus-visible:outline-ring cursor-help rounded-sm focus-visible:outline-2',
          className,
        )}
      >
        {children}
        <span className="sr-only">{hint}</span>
      </button>
    </SimpleTooltip>
  );
}

export function FirstFiveDaysCell({ cell }: { cell: FirstFiveDaysViews | undefined }) {
  const { text, progressTag, hint, muted } = firstFiveDaysDisplay(cell, new Date());
  return (
    <HintTrigger
      hint={hint}
      className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap"
    >
      <span
        className={cn(
          'text-body-sm-semibold tabular-nums',
          muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {text}
      </span>
      {progressTag && (
        <span className="bg-muted text-caption-medium text-muted-foreground rounded-full px-2 py-0.5">
          {progressTag}
        </span>
      )}
    </HintTrigger>
  );
}

const AD_TONE_CLASS: Record<AdTone, string> = {
  yes: 'rounded-full px-2.5 py-1 text-caption-medium bg-background-success-light-hover text-foreground-success-default',
  no: 'rounded-full px-2.5 py-1 text-caption-medium bg-stone-200 text-muted-foreground',
  not_found: 'rounded-full px-2.5 py-1 text-caption-medium bg-muted text-muted-foreground',
  unknown: 'text-body-sm-semibold text-muted-foreground',
};

export function AdMentionedCell({ cell }: { cell: AdDisclosureCell | undefined }) {
  const { label, hint, tone } = adMentionedDisplay(cell);
  return (
    <HintTrigger hint={hint} className={cn('inline-flex items-center', AD_TONE_CLASS[tone])}>
      {label}
    </HintTrigger>
  );
}
